import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { salesCrmApi } from "./api";
import "./salescrm.css";

const SalesCrmAdmin = () => {
  const navigate = useNavigate();
  const [perf, setPerf] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, s, src] = await Promise.all([
        salesCrmApi.performance(),
        salesCrmApi.settings(),
        salesCrmApi.sources(),
      ]);
      setPerf(p.data.results || []);
      setSettings(s.data);
      setSources(src.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Manager access required.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    setMessage(null);
    try {
      const res = await salesCrmApi.patchSettings(settings);
      setSettings(res.data);
      setMessage("Settings saved.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save settings.");
    }
  };

  const toggleSource = async (src) => {
    try {
      await salesCrmApi.patchSource({
        code: src.code,
        is_active: !src.is_active,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update source.");
    }
  };

  if (loading) {
    return (
      <div className="child-sidenav">
        <div className="childnav-content scrm-page">Loading manager console…</div>
      </div>
    );
  }

  return (
    <div className="child-sidenav">
      <div className="childnav-content scrm-page">
        <div className="scrm-header">
          <div>
            <h2>Sales CRM · Manager</h2>
            <p>Team performance, daily lead limits and source priority.</p>
          </div>
          <div className="scrm-actions">
            <button
              type="button"
              className="scrm-btn scrm-btn-outline"
              onClick={() => navigate("/dashboard/sales-crm")}
            >
              My dashboard
            </button>
            <button
              type="button"
              className="scrm-btn scrm-btn-ghost"
              onClick={() => navigate("/dashboard/sales-crm/leads?bucket=all")}
            >
              All team leads
            </button>
          </div>
        </div>

        {message && <div className="scrm-alert ok">{message}</div>}
        {error && <div className="scrm-alert err">{error}</div>}

        <div className="scrm-card">
          <h3>Employee performance today / this month</h3>
          <div className="scrm-table-wrap">
            <table className="scrm-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Fresh today</th>
                  <th>Backlog</th>
                  <th>Connected</th>
                  <th>No answer</th>
                  <th>Hot</th>
                  <th>Interested</th>
                  <th>Bought (mo)</th>
                  <th>Revenue (mo)</th>
                </tr>
              </thead>
              <tbody>
                {perf.map((row) => (
                  <tr key={row.employee.id}>
                    <td>
                      <b>{row.employee.name}</b>
                      <div className="scrm-muted">{row.employee.role}</div>
                    </td>
                    <td>{row.today_fresh}</td>
                    <td>{row.backlog}</td>
                    <td>{row.connected_today}</td>
                    <td>{row.no_answer_today}</td>
                    <td>{row.hot}</td>
                    <td>{row.interested}</td>
                    <td>{row.bought_month}</td>
                    <td>₹{row.revenue_month}</td>
                  </tr>
                ))}
                {!perf.length && (
                  <tr>
                    <td colSpan={9}>
                      <div className="scrm-empty">
                        No active sales employees yet. Staff users are enrolled
                        automatically when they open Sales CRM.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {settings && (
          <div className="scrm-card">
            <h3>CRM settings</h3>
            <div className="scrm-form scrm-form-row">
              <label>
                Default daily fresh leads
                <input
                  type="number"
                  value={settings.default_daily_lead_limit}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      default_daily_lead_limit: Number(e.target.value),
                    })
                  }
                />
              </label>
              <label>
                Connected calls target / day
                <input
                  type="number"
                  value={settings.min_connected_calls_target}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      min_connected_calls_target: Number(e.target.value),
                    })
                  }
                />
              </label>
              <label>
                Attempts before Unreachable
                <input
                  type="number"
                  value={settings.max_call_attempts_before_unreachable}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      max_call_attempts_before_unreachable: Number(e.target.value),
                    })
                  }
                />
              </label>
            </div>
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="scrm-btn scrm-btn-primary"
                onClick={saveSettings}
              >
                Save settings
              </button>
            </div>
          </div>
        )}

        <div className="scrm-card">
          <h3>Lead sources (priority order)</h3>
          <p className="scrm-muted">
            Fresh allocation walks sources from top to bottom. Existing ICC
            Profiles / Orders are only read — never duplicated.
          </p>
          <table className="scrm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Code</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  <td>{s.priority}</td>
                  <td>
                    <b>{s.name}</b>
                    <div className="scrm-muted">{s.description}</div>
                  </td>
                  <td>{s.code}</td>
                  <td>
                    <button
                      type="button"
                      className={`scrm-btn ${
                        s.is_active ? "scrm-btn-ghost" : "scrm-btn-danger"
                      }`}
                      onClick={() => toggleSource(s)}
                    >
                      {s.is_active ? "On" : "Off"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export { SalesCrmAdmin };
